import React, { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { FilteredVehicle } from '../../types';
import { VehicleCard } from './VehicleCard.styles';
import useCtaURLBuilder from '../../hooks/useCtaURLBuilder';
import T1VehicleCard from './VehicleCard-T1';
import T3VehicleCard from './VehicleCard-T3';
import useGetTier3Url from '../../hooks/Fetch/useGetTier3Url';
import { ELIGIBLE_DEALER_IDS, ELIGIBLE_DEALERS } from '../../constants/tier3EligibleDealers';
import { CarType } from '../../types/oneGraphTypes';

interface VehicleCardProps {
  vehicle: CarType;
  isTier3page: boolean;
  marketType: string;
  isImageGalleryOn: boolean;
  mainDealer?: string;
}

const VehicleResultCard: React.FC<VehicleCardProps> = ({
  vehicle,
  isTier3page,
  marketType,
  isImageGalleryOn,
  mainDealer,
}) => {
  const { t, i18n } = useTranslation();
  const lang = i18n.languages?.[0] || 'en';

  const {
    id,
    titleText,
    carPrices,
    model,
    mileage,
    dealer,
    subtitleText,
    cartypeText,
    images,
    salesInfo,
    vin,
    colorInfo,
    commissionNumber,
    driveText,
    gearText,
  } = vehicle.stockCar;

  const modelPrice = carPrices?.find((toCheck) => toCheck.type === 'list')?.price?.valueAsText;
  const vehicleType = cartypeText;
  const isAvailableNow = salesInfo.availableFromDateInfo.value === 'Immediately';
  const bodyType = null;
  const saleOrderType = salesInfo.saleOrderTypeText;
  const vehicleOrderStatus = salesInfo.orderStatusText;
  const vehicleInventoryType = cartypeText;
  const kvpsSyncId = dealer.id;
  const isUsedVehicle = cartypeText === 'U';

  const isReservedVehicle = saleOrderType?.includes('SOLD');

  const market = useMemo(() => {
    if (marketType === 'USA') return isUsedVehicle || vehicleType === 'non-audi' ? 'usuc' : 'us';

    return isUsedVehicle || vehicleType === 'non-audi' ? 'cauc' : 'ca';
  }, [isUsedVehicle, marketType, vehicleType]);

  const isMarketUSA = useMemo(() => {
    if (marketType === 'USA') return true;
    return false;
  }, [marketType]);

  const vdpUrl = useCtaURLBuilder();

  const vehicleDetailsUrl = useMemo(() => {
    const isTier3Redirect = ELIGIBLE_DEALERS.map((singleDealer) => singleDealer.dealerId).includes(
      kvpsSyncId.slice(3),
    );
    const tier3param = isTier3page || isTier3Redirect ? 'isdealer&' : '';
    const tier3VdpUrl = ELIGIBLE_DEALERS.find(
      (singleDealer) => singleDealer.dealerId === kvpsSyncId.slice(3),
    )?.vehicleUrl;
    const baseURL = isTier3page || isTier3Redirect ? tier3VdpUrl : vdpUrl;

    return `${baseURL}?${tier3param}market=${market}&vehicleId=${vin}`;
  }, [isTier3page, kvpsSyncId, market, vdpUrl, vin]);

  // SHOULD TEST LATER
  const parsedModelName = useMemo(() => {
    return titleText?.replace(/^\d{4}\s/, '');
  }, [titleText]);

  if (
    // If we get null, we hide it
    isAvailableNow === null ||
    // if availableNow is boolean false and
    // vehicleOrderStatus not one of the below three, we hide it
    (!isAvailableNow &&
      vehicleOrderStatus &&
      !['Intransit From Factory', 'Port Stock', 'Intransit Inland'].includes(vehicleOrderStatus))
    // if availableNow is boolean false and
    // saleOrderType not the one below, we hide it
    // (!isAvailableNow &&
    //   saleOrderType &&
    //   !['SHIP - Deliverable Inventory Order'].includes(saleOrderType))
  )
    return null;

  return (
    <>
      <VehicleCard
        data-component="result-tile"
        data-testid="result-tile"
        data-vehicle-model-name={parsedModelName}
        data-vehicle-body-type={bodyType}
        data-vehicle-model-year={model?.salesModelyear}
        data-vehicle-price={modelPrice}
        data-vehicle-dealer-name={dealer.name}
        data-vehicle-sale-type={
          vehicleType === 'new' && isAvailableNow
            ? 'available-now'
            : saleOrderType || vehicleInventoryType
        }
        data-vehicle-sale-order-type={
          saleOrderType === 'SOLD - Customer Sold Order' ? 'Reserved' : 'null'
        }
        data-vehicle-status={
          vehicleType === 'new' && isAvailableNow ? 'null' : vehicleOrderStatus || 'Unavailable'
        }
        data-vehicle-vin={vin}
        data-vehicle-make="Audi"
        data-vehicle-exterior-color={colorInfo?.exteriorColor?.colorInfo?.text}
        data-vehicle-stock-number={commissionNumber}
        data-vehicle-drivetrain={driveText}
        data-vehicle-gear={gearText}
        data-vehicle-trim={subtitleText}
        data-vehicle-dealer-code={kvpsSyncId}
        data-vehicle-inventory-type={vehicleType}
        data-cy="vehicle-card"
        // only on Canadian market T1 we have hover effect
        noHoverEffect={isTier3page || isMarketUSA}
        isReservedVehicle={isReservedVehicle && isTier3page}
      >
        {isTier3page ? (
          <T3VehicleCard
            isImageGalleryOn={isImageGalleryOn}
            marketType={marketType}
            vehicle={vehicle}
            vehicleDetailsUrl={vehicleDetailsUrl}
            mainDealer={mainDealer}
          />
        ) : (
          <T1VehicleCard
            isImageGalleryOn={isImageGalleryOn}
            marketType={marketType}
            vehicle={vehicle}
            vehicleDetailsUrl={vehicleDetailsUrl}
          />
        )}
      </VehicleCard>
    </>
  );
};

export default VehicleResultCard;
